import { createApi, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import * as Sentry from '@sentry/react'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { BaseRequest, BaseResponse } from '../models'
import axios from 'axios'
import { FileKind } from '../../models/File'
import { XMLParser } from 'fast-xml-parser'

const _BASE_URL = import.meta.env.REACT_APP_API_BASE_URL!

export interface UploadFileRequest {
  uploadUrl: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  uploadParameters: { [key: string]: any }
  file: File
}

export interface FileUploadRequest extends BaseRequest {
  body: {
    fileName: string
    fileSize: number
    contentType: string
    kind: FileKind
    recipientUserId?: string
    metadata?: string
  }
}

export interface FileInfo {
  fileId: string
  fileName: string
  fileSize: number
  userId: string
  contentType: string
  kind: FileKind
  metadata: string
  status: string
}

export interface FileDownloadInfoRequest extends BaseRequest {
  fileId: string
}

export interface FileDownloadInfo {
  fileInfo: FileInfo
  location: string
}

export interface FileUploadRequestResponse {
  fileInfo: FileInfo
  fileUploadUrl: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fileUploadParameters: { [key: string]: any }
}

export interface GetFileByIdRequest extends BaseRequest {
  fileId: string
}

const xmlParser = new XMLParser()

export const fileApi = createApi({
  reducerPath: 'fileApi',
  baseQuery: fetchBaseQuery({}),
  endpoints: (builder) => ({
    uploadFile: builder.mutation<void, UploadFileRequest>({
      query: (request) => {
        const formData = new FormData()
        for (const [key, value] of Object.entries(request.uploadParameters)) {
          formData.append(key, value)
        }
        formData.append('file', request.file)

        return {
          url: request.uploadUrl,
          body: formData,
          responseHandler: 'content-type',
          formData: true,
          method: 'POST',
        }
      },
      transformErrorResponse: (error, meta): FetchBaseQueryError => {
        // If RTK Query parsed the error data, use it
        if ('data' in error) {
          const data = error.data
          const contentType = meta?.response?.headers.get('content-type')

          // Parse XML responses to extract error details
          if (contentType?.includes('xml') && typeof data === 'string') {
            try {
              const parsed = xmlParser.parse(data)
              Sentry.captureException(new Error('S3 signed URL upload error', { cause: parsed?.Error }))
              return { ...error, data: parsed?.Error }
            } catch {
              return error
            }
          }
        }
        return error
      },
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    downloadFile: builder.query<any, string>({
      query: (location) => ({
        url: location,
        method: 'GET',
        responseHandler: (response) => response.json(),
      }),
    }),
  }),
})

export const accountsFileApi = createApi({
  reducerPath: 'accountsFileApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  endpoints: (builder) => ({
    downloadGlobalFileRequest: builder.query<BaseResponse<FileDownloadInfo>, FileDownloadInfoRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/files/download/${request.fileId}/`,
        }
      },
    }),
    uploadFileRequest: builder.mutation<BaseResponse<FileUploadRequestResponse>, FileUploadRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/files/upload/`,
          method: 'POST',
          body: request.body,
        }
      },
    }),
    getFileById: builder.query<FileInfo | null, GetFileByIdRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/files/${request.fileId}`,
          validateStatus: (response) => (response.status >= 200 && response.status <= 299) || response.status === 404,
        }
      },
      transformResponse: (response: BaseResponse<FileInfo> | undefined) => {
        return response?.data ?? null
      },
    }),
  }),
})

export const downloadFile = (url: string) => {
  return axios.get(url, {
    method: 'GET',
    responseType: 'blob',
  })
}

export const uploadFileWithProgress = (
  uploadUrl: string,
  uploadParameters: Record<string, string>,
  file: File,
  onProgress: (progress: number) => void,
  signal?: AbortSignal,
) =>
  new Promise<void>((resolve, reject) => {
    const formData = new FormData()
    for (const [key, value] of Object.entries(uploadParameters)) {
      formData.append(key, value)
    }
    formData.append('file', file)

    const xhr = new XMLHttpRequest()

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100))
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100)
        resolve()
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.responseText}`))
      }
    }

    xhr.onerror = () => reject(new Error('Network error during upload'))
    xhr.onabort = () => reject(new DOMException('Upload aborted', 'AbortError'))

    if (signal) {
      signal.addEventListener('abort', () => xhr.abort(), { once: true })
    }

    xhr.open('POST', uploadUrl)
    xhr.send(formData)
  })

export const { useUploadFileMutation, useDownloadFileQuery, useLazyDownloadFileQuery } = fileApi
export const {
  useDownloadGlobalFileRequestQuery,
  useLazyDownloadGlobalFileRequestQuery,
  useUploadFileRequestMutation,
  useGetFileByIdQuery,
  useLazyGetFileByIdQuery,
} = accountsFileApi
